<template>
  <top-navigation
    :title="$t('settings.group_settings')"
    :button-name="$t('global.inputs.labels.save')"
    :loading="isUpdating"
    :disabled="!isValid"
    @submit-click="handleUpdate"
  >
  </top-navigation>
  <div class="mx-auto min-h-screen pt-24">
    <information-text
      v-if="groupData"
      :information="$t('settings.change_group') + ': ' + groupData.name"
    ></information-text>
    <div class="my-4 w-full">
      <profile-picture-input
        :current-image-url="previewUrl || currentGroupPictureUrl"
        :label="$t('global.inputs.labels.group_picture')"
        :square="true"
        :large="true"
        @change="handleFileChanged"
      />
    </div>
    <div class="my-4 w-full">
      <text-input
        v-model="name"
        :placeholder="$t('global.inputs.placeholders.group_name')"
        :label="$t('global.inputs.labels.group_name') + '*'"
      />
      <p v-show="validationErrors.name" class="mt-1 text-sm text-red-600">
        {{ $t("global.inputs.errors.group_name") }}
      </p>
    </div>
    <div class="my-4 w-full">
      <text-area-input
        v-model="description"
        :placeholder="$t('global.inputs.placeholders.group_description')"
        :label="$t('global.inputs.labels.group_description')"
      />
      <p
        v-show="validationErrors.description"
        class="mt-1 text-sm text-red-600"
      >
        {{ $t("global.inputs.errors.group_description_too_long") }}
      </p>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import TopNavigation from "../../components/TopNavigation.vue";
import InformationText from "../../components/InformationText.vue";
import TextInput from "../../components/Inputs/TextInput.vue";
import TextAreaInput from "../../components/Inputs/TextAreaInput.vue";
import ProfilePictureInput from "../../components/ui/ProfilePictureInput.vue";
import { setWarningToast } from "@/helpers/notifications/toast";
import { ImageSizes } from "@/enums/images-sizes";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import {
  updateGroup,
  updateGroupPicture,
} from "@/helpers/firestore/GroupService";
import { uploadGroupPicture } from "@/helpers/firestorage/GroupPictureService";
import {
  validateGroupData,
  validateFileSize,
} from "@/helpers/utils/validate-group";

const router = useRouter();
const { t } = useI18n();
const groupsStore = useGroupsStore();
const userStore = useUserStore();

// State
const name = ref("");
const description = ref("");
const pictureFile = ref<File | null>(null);
const previewUrl = ref("");
const isUpdating = ref(false);

// Computed
const groupDocId = computed(() => userStore.activeGroup);
const groupData = computed(() => groupsStore.groupData);

const currentGroupPictureData = computed(() => {
  const id = groupDocId.value;
  if (!id) return undefined;
  return groupsStore.groupPictureData[id];
});

const currentGroupPictureUrl = computed(() => {
  const pictureData = currentGroupPictureData.value;
  if (pictureData?.[ImageSizes.SD]?.url) {
    return pictureData[ImageSizes.SD].url;
  }
  return "";
});

const validation = computed(() => {
  return validateGroupData(name.value, description.value);
});

const validationErrors = computed(() => validation.value.errors);
const isValid = computed(() => validation.value.isValid);

// Lifecycle
onMounted(() => {
  // Use existing store data
  const data = groupData.value;
  if (data) {
    name.value = data.name || "";
    description.value = data.description || "";
  }

  // Only download group picture if not already loaded
  const groupId = groupDocId.value;
  if (groupId && !groupsStore.groupPictureData[groupId]?.[ImageSizes.SD]) {
    groupsStore.downloadGroupPictureData(ImageSizes.SD);
  }
});

// Methods
function handleFileChanged(file: File) {
  const maxSizeMB = 100;

  if (!validateFileSize(file, maxSizeMB)) {
    setWarningToast({
      message: t("global.toast.file_too_big", [maxSizeMB]),
    });
    return;
  }

  pictureFile.value = file;
  previewUrl.value = URL.createObjectURL(file);
}

async function handleUpdate() {
  if (!isValid.value) {
    setWarningToast({
      message: t("global.toast.check_all_fields"),
    });
    return;
  }

  const id = groupDocId.value;
  if (!id) {
    console.error("No group ID available");
    return;
  }

  isUpdating.value = true;

  try {
    // Upload picture if changed
    if (pictureFile.value) {
      const { picturePath, blurHash } = await uploadGroupPicture(
        id,
        pictureFile.value,
      );
      await updateGroupPicture(id, picturePath, blurHash);
      groupsStore.clearGroupProfilePictureData(id);
    }

    // Update group metadata
    await updateGroup(id, {
      name: name.value || "",
      description: description.value || "",
    });

    // Refresh and navigate
    await groupsStore.refreshAllUserGroupsData();
    router.push({ name: "settings" });
  } catch (error) {
    console.error("Error updating group:", error);
  } finally {
    isUpdating.value = false;
  }
}
</script>
